import { Color, type ColorRepresentation, MeshBasicMaterial } from 'three';
import type { HexTileType } from '../../model/types';

/**
 * Base colour per tile type. Shared by the tile meshes and the HUD terrain
 * legend so the two never disagree.
 */
const TILE_COLORS: Record<HexTileType, string> = {
  // 'default' is open grassland: walkable, no resources.
  default: '#5f8a3e',
  water: '#2f6fb3',
  forest: '#2f6a34',
  // Tilled soil; the golden wheat on top (WHEAT_COLOR) is what reads as "field".
  field: '#7a5a2e',
  beach: '#d8c89a',
};

/** Colour of the wheat tufts drawn on field tiles (also the legend swatch). */
const WHEAT_COLOR = '#e6bf45';

/**
 * Fog of war: the colour of unexplored tiles, which is also the game's
 * background, so fog, background and the clouds above read as one.
 */
const FOG_COLOR = '#d7dce3';
/** Explored tiles out of sight are drawn this much darker than when in sight. */
const EXPLORED_DIM = 0.35;

/** A tile or prop colour as shown on explored tiles that aren't in sight. */
function exploredColor(color: ColorRepresentation): Color {
  return new Color(color).multiplyScalar(EXPLORED_DIM);
}

/**
 * The single shared material for unexplored (fogged) tiles. Unexplored tiles
 * still render — as flat fog — so the board stays hole-free and every tile
 * remains clickable for issuing orders. Unlit and not tone-mapped, so it
 * matches the background exactly instead of shading to grey.
 */
const unexploredMaterial = new MeshBasicMaterial({ color: FOG_COLOR, toneMapped: false });

export { exploredColor, FOG_COLOR, TILE_COLORS, unexploredMaterial, WHEAT_COLOR };
